import React from "react";

export interface CardSelectorItem {
  key: string;
  title: string;
  description?: string;
  extra?: React.ReactNode;
}

interface CardSelectorProps {
  label: string;
  items: CardSelectorItem[];
  selected: string | string[];
  onSelect: (key: string) => void;
  multiSelect?: boolean;
  getDisabled?: (key: string) => boolean;
  getExtra?: (key: string) => React.ReactNode;
  className?: string;
}

export const CardSelector: React.FC<CardSelectorProps> = ({
  label,
  items,
  selected,
  onSelect,
  multiSelect = false,
  getDisabled,
  getExtra,
  className = "",
}) => {
  const isSelected = (key: string) =>
    Array.isArray(selected) ? selected.includes(key) : selected === key;

  return (
    <div className={`space-y-2 ${className}`}>
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{label}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => {
          const disabled = getDisabled ? getDisabled(item.key) : false;
          const selectedCard = isSelected(item.key);
          return (
            <button
              key={item.key}
              type="button"
              disabled={disabled}
              className={`p-4 border rounded-lg text-left transition-all duration-150
                ${selectedCard 
                  ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30" 
                  : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700"
                }
                ${disabled 
                  ? "opacity-50 cursor-not-allowed" 
                  : "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/20"
                }
              `}
            >
              <div className="flex items-center gap-2 mb-2">
                {multiSelect ? (
                  <input
                    type="checkbox"
                    checked={selectedCard}
                    disabled={disabled}
                    onChange={() => onSelect(item.key)}
                    className="accent-blue-500 dark:accent-blue-400 w-5 h-5"
                  />
                ) : (
                  <input
                    type="radio"
                    checked={selectedCard}
                    disabled={disabled}
                    onChange={() => onSelect(item.key)}
                    className="accent-blue-500 dark:accent-blue-400 w-5 h-5"
                  />
                )}
                <span className="font-semibold text-lg text-gray-900 dark:text-gray-100">{item.title}</span>
              </div>
              {item.description && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">{item.description}</p>
              )}
              {item.extra}
              {getExtra && getExtra(item.key)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
